import { alpha, createTheme } from "@mui/material/styles";

const primary = "#2563eb";
const secondary = "#0f766e";
const accent = "#f97316";
const surface = "#ffffff";
const surfaceMuted = "#f8fafc";
const ink = "#0f172a";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: primary,
      dark: "#1d4ed8",
      light: "#60a5fa",
      contrastText: "#ffffff",
    },
    secondary: {
      main: secondary,
      dark: "#115e59",
      light: "#5eead4",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#f59e0b",
    },
    error: {
      main: "#dc2626",
    },
    success: {
      main: "#059669",
    },
    background: {
      default: "#eef4ff",
      paper: surface,
    },
    text: {
      primary: ink,
      secondary: "#475569",
    },
    divider: "rgba(148, 163, 184, 0.28)",
    custom: {
      accent,
      surfaceMuted,
      heroGlow: alpha(primary, 0.14),
      softBorder: alpha("#1e293b", 0.08),
      shadow: "0 24px 60px rgba(15, 23, 42, 0.10)",
    },
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: [
      "\"Plus Jakarta Sans\"",
      "\"Avenir Next\"",
      "\"Segoe UI\"",
      "sans-serif",
    ].join(","),
    h1: {
      fontSize: "clamp(2.8rem, 7vw, 4.8rem)",
      fontWeight: 800,
      lineHeight: 0.98,
      letterSpacing: "-0.04em",
    },
    h2: {
      fontSize: "clamp(2.1rem, 4vw, 3.1rem)",
      fontWeight: 760,
      lineHeight: 1.02,
      letterSpacing: "-0.03em",
    },
    h3: {
      fontSize: "clamp(1.5rem, 3vw, 2.05rem)",
      fontWeight: 720,
      letterSpacing: "-0.02em",
    },
    h4: {
      fontSize: "1.2rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h5: {
      fontSize: "1rem",
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: "1rem",
      lineHeight: 1.75,
      color: "#475569",
    },
    body1: {
      fontSize: "0.97rem",
      lineHeight: 1.7,
    },
    body2: {
      fontSize: "0.9rem",
      lineHeight: 1.6,
    },
    button: {
      textTransform: "none",
      fontWeight: 700,
      letterSpacing: "0.01em",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": {
          colorScheme: "light",
        },
        "*, *::before, *::after": {
          boxSizing: "border-box",
        },
        html: {
          scrollBehavior: "smooth",
        },
        body: {
          margin: 0,
          minHeight: "100vh",
          background:
            "radial-gradient(circle at top left, rgba(96,165,250,0.20), transparent 28%), radial-gradient(circle at top right, rgba(20,184,166,0.16), transparent 26%), linear-gradient(180deg, #f8fbff 0%, #eef4ff 52%, #f6f8fc 100%)",
          color: ink,
        },
        "#root": {
          minHeight: "100vh",
        },
        "::selection": {
          backgroundColor: alpha(primary, 0.2),
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 14,
          paddingInline: 18,
          minHeight: 44,
        },
        containedPrimary: {
          boxShadow: "0 14px 34px rgba(37, 99, 235, 0.22)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          maxWidth: "100%",
          "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 22,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
          border: `1px solid ${alpha("#ffffff", 0.9)}`,
          backdropFilter: "blur(12px)",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
  },
});

export default theme;
